<template>
  <Dialog :open="show" @update:open="onUpdateDialog">
    <DialogScrollContent size="xl" class="tw:overflow-hidden">
      <DialogHeader>
        <DialogTitle>
          {{ $t('Allotment') }}
        </DialogTitle>
      </DialogHeader>

      <template v-if="allotmentEdited">
        <div class="tw:flex-1 tw:border-t tw:lg:flex tw:divide-y tw:lg:divide-y-0 tw:lg:divide-x tw:-m-6 tw:mt-0">
          <div class="tw:flex-1 tw:p-6">
            <Alert v-if="allotmentEdited?.batch_uuid" variant="info">
              <strong>{{ $t('Linked allotments modification') }}</strong>
              <p class="mb-0">
                {{ $t('The “Apply to linked allotments” option is enabled by default; uncheck it if you do not wish to extend the modifications to the associated allotments.') }}
              </p>
            </Alert>

            <form v-on:submit.prevent="submitAllotment()">
              <div class="form-group">
                <label for="allotmentModalItems">{{ $t('Items') }}</label>
                <input id="allotmentModalItems"
                       type="text"
                       :value="equipments.map((equipment) => equipment.barcode_number).join(', ')"
                       class="form-control"
                       disabled
                />
              </div>

              <div class="form-group">
                <label for="allotmentModalStart">{{ $t('Start') }}</label>
                <Datetime id="allotmentModalStart"
                          v-model="allotmentEdited.start_at"
                          :minute-step="5"
                          :timezone="timezone"
                          with-timezone
                          :className="errors.start_at ? 'is-invalid' : ''">
                </Datetime>
                <div v-if="errors.start_at" class="invalid-feedback">{{ errors.start_at[0] }}</div>
              </div>
              <div class="form-group">
                <label for="allotmentModalEnd">{{ $t('End') }}</label>
                <Datetime  id="allotmentModalEnd"
                           v-model="allotmentEdited.end_at"
                           :min-datetime="allotmentEdited.start"
                           :minute-step="5"
                           :timezone="timezone"
                           with-timezone
                           :className="errors.end_at ? 'is-invalid' : ''">
                </Datetime>
                <div v-if="errors.end_at" class="invalid-feedback">{{ errors.end_at[0] }}</div>
              </div>
              <div class="form-group">
                <label for="allotmentModalStatus">{{ $t('Status') }}</label>
                <select id="allotmentModalStatus" v-model="allotmentEdited.equipment_status"
                        @change="onStatusChange"
                        :class="['form-control', errors.equipment_status ? 'is-invalid' : '']">
                  >
                  <option value="">----</option>
                  <option v-for="status in equipmentStatuses"
                          :key="status.value"
                          :value="status.value">{{status.label}}</option>
                </select>
                <div v-if="errors.equipment_status" class="invalid-feedback">{{ errors.equipment_status[0] }}</div>
              </div>
              <div class="form-group">
                <label for="allotmentModalName">{{ $t('Title') }}</label>
                <input id="allotmentModalName" type="text" v-model="allotmentEdited.title" @focus="titleChanged = true" name=""
                       :class="['form-control', errors.title ? 'is-invalid' : '']">
                <div v-if="errors.title" class="invalid-feedback">{{ errors.title[0] }}</div>
              </div>
              <div class="form-group">
                <label for="allotmentModalComment">{{ $t('Comment') }}</label>
                <textarea id="allotmentModalComment" v-model="allotmentEdited.comment" name=""
                          :class="['form-control', errors.comment ? 'is-invalid' : '']"></textarea>
                <div v-if="errors.comment" class="invalid-feedback">{{ errors.comment[0] }}</div>
              </div>
              <div class="tw:flex tw:items-center tw:gap-2 my-3">
                <Toggle id="allotmentModalAllowBooking" v-model="allotmentEdited.allow_booking" />
                <label for="allotmentModalAllowBooking">
                  {{ $t('Allow booking during this period') }}
                </label>
              </div>

              <div v-if="allotmentEdited.batch_uuid" class="tw:flex tw:items-center tw:gap-2 my-3">
                <Toggle id="allotmentModalApplyToBatch" v-model="applyToBatch" />
                <label for="allotmentModalApplyToBatch">
                  {{ $t('Apply to linked allotments') }}
                </label>
              </div>

              <div class="tw:flex tw:items-center tw:justify-between">
                <Button type="submit">{{ $t('Save') }}</Button>

                <!-- Delete Confirmation AlertDialog -->
                <template v-if="allotmentEdited?.id">
                  <AlertDialog>
                    <AlertDialogTrigger as-child>
                      <Button type="button" variant="ghost-destructive">
                        <Trash2 class="tw:size-4" />
                        {{ allotmentEdited?.batch_uuid && applyToBatch ? $t('Delete all allotments') : $t('Delete allotment') }}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{{ allotmentEdited?.batch_uuid && applyToBatch ? $t('Delete allotments') : $t('Delete allotment') }}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {{ allotmentEdited?.batch_uuid && applyToBatch
                          ? $t('This will permanently delete this allotment and all linked allotments. This action cannot be undone.')
                          : $t('This will permanently delete this allotment. This action cannot be undone.') }}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{{ $t('Cancel') }}</AlertDialogCancel>
                        <AlertDialogAction @click="deleteAllotment()" variant="destructive">
                          {{ allotmentEdited?.batch_uuid && applyToBatch ? $t('Delete all allotments') : $t('Delete allotment') }}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </template>
              </div>
            </form>
          </div>

          <!-- Side -->
          <div class="tw:flex-1">
            <div class="tw:relative tw:h-full tw:overflow-auto tw:bg-zinc-50">
              <div class="tw:lg:absolute tw:w-full tw:isolate tw:pb-6">
                <div>
                  <h5 class="tw:m-0 tw:text-base tw:lg:sticky tw:lg:z-10 tw:lg:top-0 tw:lg:bg-zinc-50/30 tw:lg:backdrop-blur-xl tw:px-6 tw:pt-6 tw:pb-2 tw:lg:py-4">{{ $t('Events') }}</h5>
                  <div class="tw:px-6 tw:pb-3">
                    <LoadingOverlay :active="isLoadingTours">
                      <ul class="list-group">
                        <li v-for="tour in tours"
                            class="list-group-item d-flex align-items-start">
                          <div>
                            <a :href="tour.links.show" target="_blank">
                              {{ tour.name }}
                              <SquareArrowOutUpRight class="tw:inline-block tw:-mt-0.5 tw:size-3" />
                            </a>
                            <small class="text-muted d-block">
                              <span v-if="tour.start_at" class="d-block"> {{ $t('Start at:') }} {{ dayjs(tour.start_at).format('lll') }}</span>
                              <span v-if="tour.end_at" class="d-block"> {{ $t('End at:') }} {{ dayjs(tour.end_at).format('lll') }}</span>
                            </small>
                          </div>
                        </li>
                      </ul>
                    </LoadingOverlay>
                  </div>
                </div>

                <div>
                  <h5 class="tw:m-0 tw:text-base tw:lg:sticky tw:lg:z-10 tw:lg:top-0 tw:lg:bg-zinc-50/30 tw:lg:backdrop-blur-xl tw:px-6 tw:pt-6 tw:pb-2 tw:lg:py-4">{{ $t('Allotments') }}</h5>
                  <div class="tw:px-6 tw:pb-3">
                    <EquipmentCalendarList
                      :histories="histories.filter((event) => ! (allotmentEdited && event.type === 'Allotment' && event.resource.id === allotmentEdited.id))"
                      :start_at="allotmentEdited.start_at"
                      :end_at="allotmentEdited.end_at"
                      class="overflow-auto"
                    />
                  </div>
                </div>

                <template v-if="activityLogSubject">
                  <div>
                    <h5 class="tw:m-0 tw:text-base tw:lg:sticky tw:lg:z-10 tw:lg:top-0 tw:lg:bg-zinc-50/30 tw:lg:backdrop-blur-xl tw:px-6 tw:pt-6 tw:pb-2 tw:lg:py-4">{{ $t('Activity') }}</h5>
                    <div class="tw:px-6">
                      <ActivityLogForSubject :subject="activityLogSubject" />
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </template>
    </DialogScrollContent>
  </Dialog>


</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import dayjs from "dayjs";
import { emit as emitEvent } from "../../eventBus.js";

import {
  History as HistoryModel,
  Tour as TourModel,
} from "../../models";
import AllotmentModel from "../../models/Allotment";
import EquipmentCalendarList from "./EquipmentCalendarList.vue";
import Datetime from "@/components/ui/Datetime.vue";
import ActivityLogForSubject from "@/components/activity-log/ActivityLogForSubject.vue";
import Alert from "@/components/ui/Alert.vue";
import { Dialog, DialogScrollContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/shadcn/components/ui/alert-dialog";
import { SquareArrowOutUpRight, Trash2 } from "@lucide/vue";
import { Button } from "@/shadcn/components/ui/button";
import LoadingOverlay from "@/components/ui/LoadingOverlay.vue";
import Toggle from "@/components/ui/Toggle.vue";

// Props
const props = defineProps({
  timezone: {
    type: String,
    required: true,
  },
  equipments: {
    type: Array,
    required: true,
    default: () => [],
  },
  selectedStart: {
    type: String,
    default: '',
  },
  selectedEnd: {
    type: String,
    default: '',
  },
  allotmentId: {
    type: Number,
    default: null,
  },
  show: {
    type: Boolean,
    default: false,
  },
  equipmentStatuses: {
    type: Array,
    default: () => [],
  },
});

// Emits
const emit = defineEmits(['close', 'update', 'deleted']);

// Reactive data
const allotmentEdited = ref(null);
const errors = ref({});
const histories = ref([]);
const tours = ref([]);
const applyToBatch = ref(true);
const isLoadingTours = ref(false);
const titleChanged = ref(false);
const showDeleteDialog = ref(false);

// Computed properties
const activityLogSubject = computed(() => {
  if (!allotmentEdited.value?.id) {
    return null;
  }
  return 'App\\Models\\Allotment:' + allotmentEdited.value.id;
});

// Methods
const onUpdateDialog = (isOpen) => {
  if (!isOpen) {
    emit('close');
  }
};

const setAllotmentEdited = async () => {
  titleChanged.value = false;
  applyToBatch.value = true;
  if (props.allotmentId === null) {
    allotmentEdited.value = new AllotmentModel({
      start_at: props.selectedStart,
      end_at: props.selectedEnd,
      equipment_status: '',
      equipments_id: props.equipments.map((equipment) => equipment.id),
    });
    return;
  }
  try {
    allotmentEdited.value = await AllotmentModel.$find(props.allotmentId);
  } catch (e) {
    alert(e);
  }
};

const onStatusChange = (event) => {
  const status = event.target.value;
  if (allotmentEdited.value.id) {
    return;
  }
  if (status === 'available') {
    allotmentEdited.value.allow_booking = true;
  }
  if (!allotmentEdited.value.title || !titleChanged.value) {
    allotmentEdited.value.title = props.equipmentStatuses.find((s) => s.value === status)?.label;
  }
};

const getHistories = async () => {
  if (!allotmentEdited.value) {
    return;
  }
  const historyModel = new HistoryModel();
  const historyResult = await historyModel
    .whereIn('equipment_id', props.equipments.map((equipment) => equipment.id))
    .where('period', [
      dayjs(allotmentEdited.value.start_at).subtract(1, 'month').set('date', 1).format('YYYY-MM-DD'),
      dayjs(allotmentEdited.value.end_at).add(1, 'month').set('date', 0).format('YYYY-MM-DD'),
    ].join(':'))
    .get();

  histories.value = historyResult.data;
};

const submitAllotment = async () => {
  try {
    let allotment = new AllotmentModel(allotmentEdited.value);
    await allotment.config({
      params: {
        apply_to_batch: applyToBatch.value,
      }
    }).save();
    errors.value = {};
    emit('update');
  } catch (error) {
    console.log('error', error);
    errors.value = error?.response?.data?.message ?? {};
  }
};

const deleteAllotment = async () => {
  if (!allotmentEdited.value.id) {
    return;
  }
  try {
    await allotmentEdited.value.config({
      params: {
        apply_to_batch: applyToBatch.value,
      }
    }).delete();
    emit('deleted');
  } catch (e) {
    alert(e);
  }
};

const getTours = async () => {
  if (!allotmentEdited.value) {
    return;
  }
  isLoadingTours.value = true;
  const tourModel = new TourModel();
  const tourResponse = await tourModel
    .whereIn('equipment_id', props.equipments.map((equipment) => equipment.id))
    .where('period', [
      dayjs(allotmentEdited.value.start_at).subtract(1, 'month').set('date', 1).format('YYYY-MM-DD'),
      dayjs(allotmentEdited.value.end_at).add(1, 'month').set('date', 0).format('YYYY-MM-DD'),
    ].join(':'))
    .get();

  tours.value = tourResponse.data;
  isLoadingTours.value = false;
};

const confirmDelete = async () => {
  showDeleteDialog.value = false;
  await deleteAllotment();
};

// Watchers
watch(() => props.show, (value) => {
  errors.value = {};
  if (value) {
    setAllotmentEdited();
  }
});

watch(allotmentEdited, (value) => {
  getHistories();
  getTours();
  emitEvent('refresh-activity-log');
});
</script>
